// src/modules/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { db, paths } from '../../firebase/config';
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  Timestamp,
  serverTimestamp,
  orderBy,
  limit
} from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  limparCPF,
  formatarData,
  getNormalizedDateString
} from '../../utils/helpers';
import { AspirantometroDashboard } from './AspirantometroDashboard';

export const AdminDashboard = () => {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('aspirantometro');

  // Estados de Pelotões, Companhias e Locais
  const [pelotoes, setPelotoes] = useState([]);
  const [companhias, setCompanhias] = useState([]); 
  const [locais, setLocais] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ nome: '' });
  const [currentItem, setCurrentItem] = useState(null);

  // Estados de CRUD de Companhias (NOVO)
  const [showModalCompanhia, setShowModalCompanhia] = useState(false);
  const [formDataCompanhia, setFormDataCompanhia] = useState({ nome: '' });
  const [currentItemCompanhia, setCurrentItemCompanhia] = useState(null);
  
  // Estados de Usuários
  const [allUsers, setAllUsers] = useState([]);
  const [escalantes, setEscalantes] = useState([]);
  const [cadetes, setCadetes] = useState([]); 
  const [cmtsPelotao, setCmtsPelotao] = useState([]); 
  const [cmtsCia, setCmtsCia] = useState([]); 
  
  // Estados de CRUD (geral)
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  // Estados de Escalante
  const [showModalEscalante, setShowModalEscalante] = useState(false);
  const [formDataEscalante, setFormDataEscalante] = useState({});
  const [currentItemEscalante, setCurrentItemEscalante] = useState(null);

  // Estados de Local
  const [showModalLocal, setShowModalLocal] = useState(false);
  const [formDataLocal, setFormDataLocal] = useState({ nome: '', tipo: 'PCO', latitude: '', longitude: '' });
  const [currentItemLocal, setCurrentItemLocal] = useState(null);

  // Estados para Gerenciadores
  const [gerenciadores, setGerenciadores] = useState([]);
  const [showModalGerenciador, setShowModalGerenciador] = useState(false);
  const [formDataGerenciador, setFormDataGerenciador] = useState({});
  const [currentItemGerenciador, setCurrentItemGerenciador] = useState(null);

  // Estados para SOE
  const [soeUsers, setSoeUsers] = useState([]);
  const [showModalSoe, setShowModalSoe] = useState(false);
  const [formDataSoe, setFormDataSoe] = useState({});
  const [currentItemSoe, setCurrentItemSoe] = useState(null);

  // Estados para Comandante de Pelotão
  const [showModalCmt, setShowModalCmt] = useState(false);
  const [formDataCmt, setFormDataCmt] = useState({ pelotoes_ids: [], pelotoes_nomes: [] });
  const [currentItemCmt, setCurrentItemCmt] = useState(null);

  // Estados para Comandante de Companhia
  const [showModalCia, setShowModalCia] = useState(false);
  const [formDataCia, setFormDataCia] = useState({});
  const [currentItemCia, setCurrentItemCia] = useState(null);
  
  // Estados para Encomendas
  const [escalasEncomendas, setEscalasEncomendas] = useState([]);
  const [showModalEncomendas, setShowModalEncomendas] = useState(false);
  const [formDataEncomendas, setFormDataEncomendas] = useState({
    data: '',
    cpf_cg: '',
    cpf_aux_cg: ''
  });
  const [currentItemEncomendas, setCurrentItemEncomendas] = useState(null);

  // --- OTIMIZAÇÃO: Carrega Logs APENAS se a aba "geral" estiver aberta ---
  useEffect(() => {
    if (activeTab !== 'geral') return;
    setLoading(true);
    const qLogs = query(collection(db, paths.logs), orderBy('criado_em', 'desc'), limit(50));
    const unsubLogs = onSnapshot(qLogs, (snapshot) => {
      const logList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLogs(logList);
      setLoading(false);
    }, (err) => {
      console.error("Erro (Logs):", err);
      setLoading(false);
    });
    return () => unsubLogs();
  }, [activeTab]);

  // --- OTIMIZAÇÃO: Carrega Usuários, Cias, Pelotões APENAS se a aba "usuarios" estiver aberta ---
  useEffect(() => {
    if (activeTab !== 'usuarios') return;
    setLoading(true);

    const unsubPelotoes = onSnapshot(collection(db, paths.pelotoes), (snapshot) => {
      setPelotoes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubCompanhias = onSnapshot(collection(db, paths.companhias), (snapshot) => {
      setCompanhias(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    
    const unsubLocais = onSnapshot(collection(db, paths.locais), (snapshot) => {
      setLocais(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qUsers = query(collection(db, paths.users));
    const unsubUsers = onSnapshot(qUsers, (snapshot) => {
        const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllUsers(users); 
        setEscalantes(users.filter(u => u.nivel === 'escalante'));
        setCadetes(users.filter(u => u.nivel === 'cadete' || u.nivel === 'escalante'));
        setGerenciadores(users.filter(u => u.nivel === 'gerenciador'));
        setSoeUsers(users.filter(u => u.nivel === 'soe'));
        setCmtsPelotao(users.filter(u => u.nivel === 'cmt_pelotao')); 
        setCmtsCia(users.filter(u => u.nivel === 'cmt_cia')); 
        setLoading(false); // Só desativa o loading depois que a tabela mais pesada baixar
    }, (err) => {
        console.error("Erro (Usuários):", err);
        setLoading(false);
    });

    return () => {
      unsubPelotoes();
      unsubCompanhias(); 
      unsubLocais(); 
      unsubUsers();
    };
  }, [activeTab]);

  // --- OTIMIZAÇÃO: Carrega Escalas APENAS se a aba "encomendas" estiver aberta ---
  useEffect(() => {
    if (activeTab !== 'encomendas') return;
    setLoading(true);
    const qEscalas = query(collection(db, paths.escalasEncomendas));
    const unsubEscalas = onSnapshot(qEscalas, (snapshot) => {
        const escalas = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.data?.toDate() || 0) - (a.data?.toDate() || 0)); 
        setEscalasEncomendas(escalas);
        setLoading(false);
    }, (err) => {
        console.error("Erro (Escalas Encomendas):", err);
        setLoading(false);
    });
    return () => unsubEscalas();
  }, [activeTab]);

  // Se for Aspirantometro, remove o loading do Admin e deixa o filho carregar
  useEffect(() => {
      if (activeTab === 'aspirantometro') setLoading(false);
  }, [activeTab]);
  
  // OTIMIZAÇÃO: Busca pontual apenas se uma exclusão for acionada (Remove o onSnapshot gigante)
  const handleResetFilaAfterDeletion = async (pelotaoId, deletedCadeteId) => {
      // Busca os cadetes daquele pelotão específico
      const qUsers = query(collection(db, paths.users), where("pelotao_id", "==", pelotaoId));
      const snapUsers = await getDocs(qUsers);
      const cadetesDoPelotao = snapUsers.docs.map(d => ({id: d.id, ...d.data()})).filter(c => c.nivel === 'cadete' || c.nivel === 'escalante');

      // Busca as escolhas daquele pelotão específico
      const qEscolhas = query(collection(db, paths.escolhas), where("pelotao_id", "==", pelotaoId));
      const snapEscolhas = await getDocs(qEscolhas);
      const cadeteIdsConcluidos = new Set(snapEscolhas.docs.map(e => e.data().cadete_id));
      
      const todosConcluidos = cadetesDoPelotao.every(c => cadeteIdsConcluidos.has(c.id));
      const deletedCadete = cadetesDoPelotao.find(c => c.id === deletedCadeteId);

      if (!deletedCadete) return;

      if (todosConcluidos) {
          await logAuditoria(userProfile, "Re-enquadramento de Fila (Rodada Fechada)", { 
              cadete: deletedCadete.nome, 
              pelotao: deletedCadete.pelotao_nome, 
              motivo: "Escolha excluída. Cadete será o próximo a escolher, reabrindo a rodada." 
          });
      } else {
          await logAuditoria(userProfile, "Re-enquadramento de Fila (Rodada Aberta)", { 
              cadete: deletedCadete.nome, 
              pelotao: deletedCadete.pelotao_nome, 
              motivo: "Escolha excluída. Cadete será o próximo a escolher, respeitando a prioridade na fila."
          });
      }
  }
  
  // ----------------------------------------------------------------------
  // MANIPULADORES DE PELOTÃO E COMPANHIA
  // ----------------------------------------------------------------------
  const handleOpenModal = (item = null) => {
    setCurrentItem(item);
    setFormData(item ? { nome: item.nome } : { nome: '' });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      if (currentItem) {
        const docRef = doc(db, paths.pelotoes, currentItem.id);
        await updateDoc(docRef, formData);
        await logAuditoria(userProfile, "Atualização Pelotão", formData);
      } else {
        const newDocRef = doc(collection(db, paths.pelotoes));
        const dataToSave = { ...formData, id: newDocRef.id };
        await setDoc(newDocRef, dataToSave);
        await logAuditoria(userProfile, "Criação Pelotão", dataToSave);
      }
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenModalCompanhia = (item = null) => {
    setCurrentItemCompanhia(item);
    setFormDataCompanhia(item ? { nome: item.nome } : { nome: '' });
    setError('');
    setShowModalCompanhia(true);
  };

  const handleSubmitCompanhia = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      if (currentItemCompanhia) {
        const docRef = doc(db, paths.companhias, currentItemCompanhia.id);
        await updateDoc(docRef, formDataCompanhia);
        await logAuditoria(userProfile, "Atualização Companhia", formDataCompanhia);
      } else {
        const newDocRef = doc(collection(db, paths.companhias));
        const dataToSave = { ...formDataCompanhia, id: newDocRef.id };
        await setDoc(newDocRef, dataToSave);
        await logAuditoria(userProfile, "Criação Companhia", dataToSave);
      }
      setShowModalCompanhia(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  // ----------------------------------------------------------------------
  // OUTROS MANIPULADORES (ESCALANTE, LOCAIS, SOE, ETC)
  // ----------------------------------------------------------------------
  const handleOpenModalEscalante = (item = null) => {
    setCurrentItemEscalante(item);
    setError('');
    if(item) {
      setFormDataEscalante(item);
    } else {
      setFormDataEscalante({ nome: '', cpf: '', pelotao_id: '', nivel: 'escalante', matricula: '', ordem_antiguidade: '' });
    }
    setShowModalEscalante(true);
  };
  
  const handleFormChangeEscalante = (e) => {
    const { name, value } = e.target;
    setFormDataEscalante(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitEscalante = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataEscalante };
      const cpfLimpo = limparCPF(dataToSave.cpf); 
      if (cpfLimpo.length !== 11) throw new Error("CPF deve conter 11 dígitos.");
      dataToSave.cpf = cpfLimpo;
      
      if (dataToSave.matricula.length < 6) throw new Error("RE deve ter 6 dígitos ou mais.");
      const ordem = parseInt(dataToSave.ordem_antiguidade, 10);
      if (isNaN(ordem) || ordem < 1 || ordem > 35) throw new Error("A Ordem de Antiguidade (número) deve ser entre 1 e 35.");
      dataToSave.ordem_antiguidade = ordem;
      
      const pelotao = pelotoes.find(p => p.id === dataToSave.pelotao_id);
      if (!pelotao) throw new Error("Pelotão inválido.");
      dataToSave.pelotao_nome = pelotao.nome;
      
      if (!currentItemEscalante || currentItemEscalante.ordem_antiguidade !== dataToSave.ordem_antiguidade) {
          const qOrdem = query(collection(db, paths.users), 
            where("pelotao_id", "==", dataToSave.pelotao_id), 
            where("ordem_antiguidade", "==", dataToSave.ordem_antiguidade)
          );
          const snapOrdem = await getDocs(qOrdem);
          if (!snapOrdem.empty) throw new Error("Este número de ordem (posição) já está em uso neste pelotão.");
      }
      
      if (currentItemEscalante) {
          const docRef = doc(db, paths.users, currentItemEscalante.id);
          delete dataToSave.id; 
          await updateDoc(docRef, dataToSave);
          await logAuditoria(userProfile, "Atualização Escalante", dataToSave);
      } else {
          const qCpf = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
          const snapCpf = await getDocs(qCpf);
          if (!snapCpf.empty) throw new Error("Este CPF já está cadastrado no sistema.");
          dataToSave.senha = cpfLimpo;
          const newDocRef = doc(collection(db, paths.users));
          dataToSave.id = newDocRef.id;
          await setDoc(newDocRef, dataToSave);
          await logAuditoria(userProfile, "Criação Escalante", dataToSave);
      }
      setShowModalEscalante(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleOpenModalLocal = (item = null) => {
    setCurrentItemLocal(item);
    setFormDataLocal(item 
        ? { nome: item.nome, tipo: item.tipo, latitude: item.latitude || '', longitude: item.longitude || '' } 
        : { nome: '', tipo: 'PCO', latitude: '', longitude: '' });
    setError('');
    setShowModalLocal(true);
  };

  const handleFormChangeLocal = (e) => {
    const { name, value } = e.target;
    setFormDataLocal(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataLocal };
      dataToSave.latitude = dataToSave.latitude.trim();
      dataToSave.longitude = dataToSave.longitude.trim();

      if (currentItemLocal) {
        const docRef = doc(db, paths.locais, currentItemLocal.id);
        delete dataToSave.id;
        await updateDoc(docRef, dataToSave);
        await logAuditoria(userProfile, "Atualização Local", dataToSave);
      } else {
        const newDocRef = doc(collection(db, paths.locais));
        dataToSave.id = newDocRef.id;
        await setDoc(newDocRef, dataToSave);
        await logAuditoria(userProfile, "Criação Local", dataToSave);
      }
      setShowModalLocal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenModalGerenciador = (item = null) => {
    setCurrentItemGerenciador(item);
    setError('');
    if(item) {
      setFormDataGerenciador(item);
    } else {
      setFormDataGerenciador({ nome: '', cpf: '', nivel: 'gerenciador' });
    }
    setShowModalGerenciador(true);
  };
  
  const handleFormChangeGerenciador = (e) => {
    const { name, value } = e.target;
    setFormDataGerenciador(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitGerenciador = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataGerenciador };
      const cpfLimpo = limparCPF(dataToSave.cpf); 
      if (cpfLimpo.length !== 11) throw new Error("CPF deve conter 11 dígitos.");
      dataToSave.cpf = cpfLimpo;
      
      dataToSave.nivel = 'gerenciador';
      delete dataToSave.pelotao_id;
      delete dataToSave.pelotao_nome;
      delete dataToSave.ordem_antiguidade;
      
      if (currentItemGerenciador) {
          const docRef = doc(db, paths.users, currentItemGerenciador.id);
          delete dataToSave.id; 
          await updateDoc(docRef, dataToSave);
          await logAuditoria(userProfile, "Atualização Gerenciador", dataToSave);
      } else {
          const qCpf = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
          const snapCpf = await getDocs(qCpf);
          if (!snapCpf.empty) throw new Error("Este CPF já está cadastrado no sistema.");
          dataToSave.senha = cpfLimpo; 
          const newDocRef = doc(collection(db, paths.users));
          dataToSave.id = newDocRef.id;
          await setDoc(newDocRef, dataToSave);
          await logAuditoria(userProfile, "Criação Gerenciador", dataToSave);
      }
      setShowModalGerenciador(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenModalSoe = (item = null) => {
    setCurrentItemSoe(item);
    setError('');
    if(item) {
      setFormDataSoe(item);
    } else {
      setFormDataSoe({ nome: '', cpf: '', nivel: 'soe' });
    }
    setShowModalSoe(true);
  };
  
  const handleFormChangeSoe = (e) => {
    const { name, value } = e.target;
    setFormDataSoe(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitSoe = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataSoe };
      const cpfLimpo = limparCPF(dataToSave.cpf);
      if (cpfLimpo.length !== 11) throw new Error("CPF deve conter 11 dígitos.");
      dataToSave.cpf = cpfLimpo;
      
      dataToSave.nivel = 'soe'; 
      delete dataToSave.pelotao_id;
      delete dataToSave.pelotao_nome;
      delete dataToSave.ordem_antiguidade;
      
      if (currentItemSoe) {
          const docRef = doc(db, paths.users, currentItemSoe.id);
          delete dataToSave.id; 
          await updateDoc(docRef, dataToSave);
          await logAuditoria(userProfile, "Atualização Usuário SOE", dataToSave);
      } else {
          const qCpf = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
          const snapCpf = await getDocs(qCpf);
          if (!snapCpf.empty) throw new Error("Este CPF já está cadastrado no sistema.");
          dataToSave.senha = cpfLimpo; 
          const newDocRef = doc(collection(db, paths.users));
          dataToSave.id = newDocRef.id;
          await setDoc(newDocRef, dataToSave);
          await logAuditoria(userProfile, "Criação Usuário SOE", dataToSave);
      }
      setShowModalSoe(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ----------------------------------------------------------------------
  // MANIPULADORES CMT CIA E CMT PELOTÃO
  // ----------------------------------------------------------------------
  const handleOpenModalCia = (item = null) => {
    setCurrentItemCia(item);
    setError('');
    if(item) {
      setFormDataCia(item);
    } else {
      setFormDataCia({ nome: '', cpf: '', cia_id: '', nivel: 'cmt_cia' });
    }
    setShowModalCia(true);
  };

  const handleFormChangeCia = (e) => {
    const { name, value } = e.target;
    setFormDataCia(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitCia = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataCia };
      const cpfLimpo = limparCPF(dataToSave.cpf);
      if (cpfLimpo.length !== 11) throw new Error("CPF deve conter 11 dígitos.");
      dataToSave.cpf = cpfLimpo;
      dataToSave.nivel = 'cmt_cia';
      
      if (!dataToSave.cia_id) {
        throw new Error("Selecione a Companhia comandada.");
      }

      // Procura a companhia selecionada para salvar o nome também
      const companhiaSelecionada = companhias.find(c => c.id === dataToSave.cia_id);
      if (companhiaSelecionada) {
        dataToSave.cia = companhiaSelecionada.nome;
      }

      if (currentItemCia) {
          const docRef = doc(db, paths.users, currentItemCia.id);
          delete dataToSave.id;
          await updateDoc(docRef, dataToSave);
          await logAuditoria(userProfile, "Atualização Cmt Companhia", dataToSave);
      } else {
          const qCpf = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
          const snapCpf = await getDocs(qCpf);
          if (!snapCpf.empty) throw new Error("CPF já cadastrado.");
          
          dataToSave.senha = cpfLimpo;
          const newDocRef = doc(collection(db, paths.users));
          dataToSave.id = newDocRef.id;
          await setDoc(newDocRef, dataToSave);
          await logAuditoria(userProfile, "Criação Cmt Companhia", dataToSave);
      }
      setShowModalCia(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenModalCmt = (item = null) => {
    setCurrentItemCmt(item);
    setError('');
    if(item) {
      let pelIds = Array.isArray(item.pelotoes_ids) ? item.pelotoes_ids : [];
      let pelNomes = Array.isArray(item.pelotoes_nomes) ? item.pelotoes_nomes : [];
      if (item.pelotao_id && pelIds.length === 0) {
        pelIds = [item.pelotao_id];
        pelNomes = [item.pelotao_nome];
      }
      setFormDataCmt({ ...item, pelotoes_ids: pelIds, pelotoes_nomes: pelNomes });
    } else {
      setFormDataCmt({ nome: '', cpf: '', pelotoes_ids: [], pelotoes_nomes: [], nivel: 'cmt_pelotao' });
    }
    setShowModalCmt(true);
  };

  const handleFormChangeCmt = (e) => {
    const { name, value } = e.target;
    setFormDataCmt(prev => ({ ...prev, [name]: value }));
  };

  const handleTogglePelotaoCmt = (pelotaoId, pelotaoNome) => {
    const currentIds = Array.isArray(formDataCmt.pelotoes_ids) ? formDataCmt.pelotoes_ids : [];
    const currentNomes = Array.isArray(formDataCmt.pelotoes_nomes) ? formDataCmt.pelotoes_nomes : [];
    
    if (currentIds.includes(pelotaoId)) {
        setFormDataCmt({
            ...formDataCmt,
            pelotoes_ids: currentIds.filter(id => id !== pelotaoId),
            pelotoes_nomes: currentNomes.filter(n => n !== pelotaoNome)
        });
    } else {
        setFormDataCmt({
            ...formDataCmt,
            pelotoes_ids: [...currentIds, pelotaoId],
            pelotoes_nomes: [...currentNomes, pelotaoNome]
        });
    }
  };

  const handleSubmitCmt = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      let dataToSave = { ...formDataCmt };
      const cpfLimpo = limparCPF(dataToSave.cpf);
      if (cpfLimpo.length !== 11) throw new Error("CPF deve conter 11 dígitos.");
      dataToSave.cpf = cpfLimpo;
      dataToSave.nivel = 'cmt_pelotao';

      if (!Array.isArray(dataToSave.pelotoes_ids) || dataToSave.pelotoes_ids.length === 0) {
        throw new Error("Selecione pelo menos um pelotão.");
      }

      if (currentItemCmt) {
          const docRef = doc(db, paths.users, currentItemCmt.id);
          delete dataToSave.id;
          await updateDoc(docRef, dataToSave);
          await logAuditoria(userProfile, "Atualização Cmt Pelotão", dataToSave);
      } else {
          const qCpf = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
          const snapCpf = await getDocs(qCpf);
          if (!snapCpf.empty) throw new Error("CPF já cadastrado.");
          
          dataToSave.senha = cpfLimpo;
          const newDocRef = doc(collection(db, paths.users));
          dataToSave.id = newDocRef.id;
          await setDoc(newDocRef, dataToSave);
          await logAuditoria(userProfile, "Criação Cmt Pelotão", dataToSave);
      }
      setShowModalCmt(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenModalEncomendas = (item = null) => {
    setError('');
    if (item) {
        setCurrentItemEncomendas(item);
        setFormDataEncomendas({
            data: getNormalizedDateString(item.data), 
            cpf_cg: item.cpf_cg,
            cpf_aux_cg: item.cpf_aux_cg
        });
    } else {
        setCurrentItemEncomendas(null);
        setFormDataEncomendas({ data: '', cpf_cg: '', cpf_aux_cg: '' });
    }
    setShowModalEncomendas(true);
  };

  const handleFormChangeEncomendas = (e) => {
    const { name, value } = e.target;
    let valorFinal = value;
    if (name === 'cpf_cg' || name === 'cpf_aux_cg') {
        valorFinal = limparCPF(value); 
        if (valorFinal.length > 11) {
            valorFinal = valorFinal.slice(0, 11);
        }
    }
    setFormDataEncomendas(prev => ({ ...prev, [name]: valorFinal }));
  };

  const handleSubmitEncomendas = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    
    const { data, cpf_cg, cpf_aux_cg } = formDataEncomendas;

    try {
        if (!data) {
            throw new Error("A data da escala é obrigatória.");
        }
        if (limparCPF(cpf_cg).length !== 11 || limparCPF(cpf_aux_cg).length !== 11) { 
            throw new Error("Ambos os CPFs devem conter 11 dígitos.");
        }

        const docId = data;
        const docRef = doc(db, paths.escalasEncomendas, docId); 

        const dataToSave = {
            id: docId,
            data: Timestamp.fromDate(new Date(data + 'T12:00:00Z')), 
            cpf_cg: limparCPF(cpf_cg), 
            cpf_aux_cg: limparCPF(cpf_aux_cg), 
            cadastrado_por_id: userProfile.id,
            cadastrado_por_nome: userProfile.nome,
            criado_em: serverTimestamp()
        };

        await setDoc(docRef, dataToSave);
        await logAuditoria(userProfile, "Cadastro/Atualização de Escala Encomendas", dataToSave);
        
        setShowModalEncomendas(false);

    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const confirmDelete = (pathKey, id) => {
    let finalPathKey = pathKey;
    if (pathKey === 'escalante') finalPathKey = 'users';
    if (pathKey === 'local') finalPathKey = 'locais'; 
    if (pathKey === 'escolha') finalPathKey = 'escolhas';
    if (pathKey === 'gerenciador') finalPathKey = 'users'; 
    if (pathKey === 'escala_encomendas') finalPathKey = 'escalasEncomendas';
    if (pathKey === 'soe') finalPathKey = 'users';
    if (pathKey === 'cmt_pelotao') finalPathKey = 'users';
    if (pathKey === 'cmt_cia') finalPathKey = 'users';
    if (pathKey === 'companhia') finalPathKey = 'companhias'; 
    
    setDeleteAction({ pathKey: finalPathKey, id, itemType: pathKey });
    setShowConfirm(true);
  };
  
  const handleDelete = async () => {
    if (!deleteAction) return;
    const { pathKey, id, itemType } = deleteAction;
    
    try {
      if (pathKey === 'pelotoes') {
        const q1 = query(collection(db, paths.users), where("pelotao_id", "==", id));
        const snap1 = await getDocs(q1);
        if (!snap1.empty) {
          throw new Error("Não é possível excluir. Existem usuários (cadetes/escalantes) associados a este pelotão.");
        }
      }

      if (pathKey === 'companhias') {
        const q1 = query(collection(db, paths.users), where("cia_id", "==", id));
        const snap1 = await getDocs(q1);
        if (!snap1.empty) {
          throw new Error("Não é possível excluir. Existem comandantes associados a esta companhia.");
        }
      }
      
      if (pathKey === 'users' && (itemType === 'escalante' || itemType === 'gerenciador' || itemType === 'soe' || itemType === 'cmt_pelotao' || itemType === 'cmt_cia')) {
        if (itemType === 'escalante') {
            const q = query(collection(db, paths.escolhas), where("cadete_id", "==", id));
            const snap = await getDocs(q);
            if (!snap.empty) {
                throw new Error("Não é possível excluir. Este usuário (escalante) possui escolhas associadas.");
            }
        }
      }
      
      if (pathKey === 'locais') {
        const q = query(collection(db, paths.datasEstagio), where("local_id", "==", id));
        const snap = await getDocs(q);
        if (!snap.empty) {
          throw new Error("Não é possível excluir. Este local está sendo usado em Datas de Estágio.");
        }
      }
      
      if (pathKey === 'escolhas') {
          const escolhaDoc = await getDoc(doc(db, paths.escolhas, id));
          if (!escolhaDoc.exists()) throw new Error("Escolha não encontrada.");
          const escolhaData = escolhaDoc.data();
          const cadeteId = escolhaData.cadete_id;
          const pelotaoId = escolhaData.pelotao_id;
          
          await deleteDoc(doc(db, paths.escolhas, id));
          
          await handleResetFilaAfterDeletion(pelotaoId, cadeteId);
      } else {
        await deleteDoc(doc(db, paths[pathKey], id));
      }
      
      await logAuditoria(userProfile, `Exclusão de Item (${itemType})`, { path: pathKey, id });
      setShowConfirm(false);
      setDeleteAction(null);
      setError('');
      
    } catch (err) {
      console.error("Erro ao excluir:", err);
      setError(err.message);
      setShowConfirm(false);
    }
  };
  
  const handleSeedDatabase = async () => {
    alert("Seed desabilitado nesta visualização.");
  };

  if (loading) return <LoadingSpinner />; 
  
  const renderGerenciarGeral = () => (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="text-xl font-semibold mb-4">Logs de Auditoria (Recentes)</h3>
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{log.criado_em ? formatarData(log.criado_em) : '...'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700">{log.usuario_nome}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700" title={log.payload}>{log.acao}</td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  );
  
  const renderGerenciarUsuarios = () => (
    <div className="space-y-6">
      
      {/* BLOCO PARA GERENCIAR AS COMPANHIAS */}
      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Companhias (Lista de Opções)</h3>
          <Button onClick={() => handleOpenModalCompanhia()} variant="success">Nova Companhia</Button>
        </div>
        <ul className="divide-y divide-gray-200">
          {companhias.map(c => (
            <li key={c.id} className="py-2 flex justify-between items-center">
              <div>
                <p className="text-gray-700 font-bold">{c.nome}</p> 
              </div>
              <div className="space-x-2">
                <Button onClick={() => handleOpenModalCompanhia(c)} variant="outline">Editar</Button>
                <Button onClick={() => confirmDelete('companhia', c.id)} variant="danger">Excluir</Button>
              </div>
            </li>
          ))}
          {companhias.length === 0 && <p className="text-gray-500 text-center py-2">Nenhuma companhia cadastrada. Adicione uma para aparecer no formulário de Cmt Cia.</p>}
        </ul>
      </div>

      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Gerenciadores (Acadêmico)</h3>
          <Button onClick={() => handleOpenModalGerenciador()} variant="success">Novo Gerenciador</Button>
        </div>
        <ul className="divide-y divide-gray-200">
          {gerenciadores.map(g => (
            <li key={g.id} className="py-2 flex justify-between items-center">
              <div>
                <p className="text-gray-700">{g.nome}</p> 
                <p className="text-sm text-gray-500">CPF: {g.cpf}</p>
              </div>
              <div className="space-x-2">
                <Button onClick={() => handleOpenModalGerenciador(g)} variant="outline">Editar</Button>
                <Button onClick={() => confirmDelete('gerenciador', g.id)} variant="danger" disabled={g.id === userProfile.id}>Excluir</Button>
              </div>
            </li>
          ))}
          {gerenciadores.length === 0 && <p className="text-gray-500 text-center py-2">Nenhum gerenciador cadastrado.</p>}
        </ul>
      </div>

      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Usuários SOE (Gestão de Estágios)</h3>
          <Button onClick={() => handleOpenModalSoe()} variant="success">Novo Usuário SOE</Button>
        </div>
        <ul className="divide-y divide-gray-200">
          {soeUsers.map(s => (
            <li key={s.id} className="py-2 flex justify-between items-center">
              <div>
                <p className="text-gray-700">{s.nome}</p> 
                <p className="text-sm text-gray-500">CPF: {s.cpf}</p>
              </div>
              <div className="space-x-2">
                <Button onClick={() => handleOpenModalSoe(s)} variant="outline">Editar</Button>
                <Button onClick={() => confirmDelete('soe', s.id)} variant="danger" disabled={s.id === userProfile.id}>Excluir</Button>
              </div>
            </li>
          ))}
          {soeUsers.length === 0 && <p className="text-gray-500 text-center py-2">Nenhum usuário SOE cadastrado.</p>}
        </ul>
      </div>

      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Comandantes de Companhia (Cmt Cia)</h3>
          <Button onClick={() => handleOpenModalCia()} variant="success">Novo Comandante Cia</Button>
        </div>
        <ul className="divide-y divide-gray-200">
          {cmtsCia.map(c => (
            <li key={c.id} className="py-2 flex justify-between items-center">
              <div>
                <p className="text-gray-700">{c.nome} - <span className="font-bold">{c.cia || 'Companhia não informada'}</span></p> 
                <p className="text-sm text-gray-500">CPF: {c.cpf}</p>
              </div>
              <div className="space-x-2">
                <Button onClick={() => handleOpenModalCia(c)} variant="outline">Editar</Button>
                <Button onClick={() => confirmDelete('cmt_cia', c.id)} variant="danger" disabled={c.id === userProfile.id}>Excluir</Button>
              </div>
            </li>
          ))}
          {cmtsCia.length === 0 && <p className="text-gray-500 text-center py-2">Nenhum comandante de companhia cadastrado.</p>}
        </ul>
      </div>

      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Comandantes de Pelotão</h3>
          <Button onClick={() => handleOpenModalCmt()} variant="success">Novo Comandante</Button>
        </div>
        <ul className="divide-y divide-gray-200">
          {cmtsPelotao.map(c => {
            let nomesDosPelotoes = 'Nenhum pelotão atribuído';
            if (Array.isArray(c.pelotoes_nomes) && c.pelotoes_nomes.length > 0) {
              nomesDosPelotoes = c.pelotoes_nomes.join(', ');
            } else if (c.pelotao_nome) {
              nomesDosPelotoes = c.pelotao_nome;
            }

            return (
              <li key={c.id} className="py-2 flex justify-between items-center">
                <div>
                  <p className="text-gray-700">
                    {c.nome} - <span className="font-bold">{nomesDosPelotoes}</span>
                  </p> 
                  <p className="text-sm text-gray-500">CPF: {c.cpf}</p>
                </div>
                <div className="space-x-2">
                  <Button onClick={() => handleOpenModalCmt(c)} variant="outline">Editar</Button>
                  <Button onClick={() => confirmDelete('cmt_pelotao', c.id)} variant="danger" disabled={c.id === userProfile.id}>Excluir</Button>
                </div>
              </li>
            );
          })}
          {cmtsPelotao.length === 0 && <p className="text-gray-500 text-center py-2">Nenhum comandante cadastrado.</p>}
        </ul>
      </div>
      
      <div className="p-4 bg-white rounded-lg shadow">
        <h3 className="text-xl font-semibold mb-4">Administrador do Sistema</h3>
        <div className="bg-gray-50 p-3 rounded">
            <p className="font-semibold">{userProfile.nome}</p>
            <p className="text-sm text-gray-600">CPF: {userProfile.cpf} (Não editável/Excluível)</p>
        </div>
      </div>
    </div>
  );
  
  const renderGerenciarEncomendas = () => (
    <div className="space-y-6">
      <div className="p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Gerenciar Escala (Encomendas)</h3>
          <Button onClick={() => handleOpenModalEncomendas()} variant="success">Nova Escala</Button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
            Cadastre a escala de serviço para acesso ao módulo de encomendas. O acesso é válido para o dia da escala e o dia seguinte.
            Se cadastrar uma escala para um dia que já existe, a anterior será **substituída**.
        </p>
        <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data da Escala</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPF Cmt Guarda</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPF Aux Cmt Guarda</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {escalasEncomendas.map(e => (
                        <tr key={e.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatarData(e.data)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{e.cpf_cg}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{e.cpf_aux_cg}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                <Button onClick={() => handleOpenModalEncomendas(e)} variant="outline" className="text-xs py-1 px-2">Editar</Button>
                                <Button onClick={() => confirmDelete('escala_encomendas', e.id)} variant="danger" className="text-xs py-1 px-2">Excluir</Button>
                            </td>
                        </tr>
                    ))}
                    {escalasEncomendas.length === 0 && (<tr><td colSpan="4" className="text-center py-4 text-gray-500">Nenhuma escala de encomendas cadastrada.</td></tr>)}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
  
  return (
    <div>
      <header className="mb-6"></header>
      
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Painel do Administrador</h2>
      <p className="text-gray-600 mb-6">Usuário: {userProfile.nome}</p>

      <div className="mb-6">
        <Button onClick={handleSeedDatabase} variant="danger">Popular Dados (SEED)</Button>
      </div>

      {error && !showModal && !showModalEscalante && !showConfirm && !showModalLocal && !showModalGerenciador && !showModalEncomendas && !showModalSoe && !showModalCmt && !showModalCia && !showModalCompanhia && (
        <div className={`mb-4 p-3 border rounded-lg ${error.startsWith('Falha') || error.startsWith('Não é possível') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-blue-100 border-blue-400 text-blue-700'}`}>
          <strong>Aviso:</strong> {error}
          <button onClick={() => setError('')} className="ml-4 font-bold float-right">&times;</button>
        </div>
      )}
      
      <nav className="flex space-x-1 mb-6 rounded-lg bg-gray-200 p-1 max-w-4xl">
        <button onClick={() => setActiveTab('aspirantometro')} className={`w-full px-4 py-2 rounded-md font-medium ${activeTab === 'aspirantometro' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Aspirantômetro</button>
        <button onClick={() => setActiveTab('encomendas')} className={`w-full px-4 py-2 rounded-md font-medium ${activeTab === 'encomendas' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Encomendas</button>
        <button onClick={() => setActiveTab('geral')} className={`w-full px-4 py-2 rounded-md font-medium ${activeTab === 'geral' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Logs do sistema</button>
        <button onClick={() => setActiveTab('usuarios')} className={`w-full px-4 py-2 rounded-md font-medium ${activeTab === 'usuarios' ? 'bg-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Usuários</button>
      </nav>
        
        <main>
          {activeTab === 'aspirantometro' && <AspirantometroDashboard />}
          {activeTab === 'encomendas' && renderGerenciarEncomendas()}
          {activeTab === 'geral' && renderGerenciarGeral()}
          {activeTab === 'usuarios' && renderGerenciarUsuarios()}
        </main>
        
      {/* Modais Existentes */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title={currentItem ? 'Editar Pelotão' : 'Novo Pelotão'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label>Nome do Pelotão</label><input name="nome" value={formData.nome || ''} onChange={(e) => setFormData({ nome: e.target.value })} required className="w-full border p-2 rounded" /></div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModal(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>

      {/* Modal de Companhia */}
      <Modal show={showModalCompanhia} onClose={() => setShowModalCompanhia(false)} title={currentItemCompanhia ? 'Editar Companhia' : 'Nova Companhia'} size="md">
        <form onSubmit={handleSubmitCompanhia} className="space-y-4">
          <div><label>Nome da Companhia (Ex: 1ª Cia)</label><input name="nome" value={formDataCompanhia.nome || ''} onChange={(e) => setFormDataCompanhia({ nome: e.target.value })} required className="w-full border p-2 rounded" /></div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalCompanhia(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>
      
      <Modal show={showModalEscalante} onClose={() => setShowModalEscalante(false)} title={currentItemEscalante ? 'Editar Escalante' : 'Novo Escalante'} size="md">
        <form onSubmit={handleSubmitEscalante} className="space-y-4">
          <div><label>Nome</label><input name="nome" value={formDataEscalante.nome || ''} onChange={handleFormChangeEscalante} required className="w-full border p-2 rounded" /></div>
          <div><label>CPF (só números)</label><input name="cpf" value={formDataEscalante.cpf || ''} onChange={handleFormChangeEscalante} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItemEscalante && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
          <div><label>Pelotão</label><select name="pelotao_id" value={formDataEscalante.pelotao_id || ''} onChange={handleFormChangeEscalante} required className="w-full border p-2 rounded"><option value="">Selecione...</option>{pelotoes.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}</select></div>
          <div><label>RE (Registro de Ensino) (6 dígitos)</label><input name="matricula" value={formDataEscalante.matricula || ''} onChange={handleFormChangeEscalante} required maxLength="6" className="w-full border p-2 rounded" /></div>
          <div><label>Ordem de Antiguidade (1-35)</label><input name="ordem_antiguidade" type="number" value={formDataEscalante.ordem_antiguidade || ''} onChange={handleFormChangeEscalante} required min="1" max="35" className="w-full border p-2 rounded" /></div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalEscalante(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>
      
      <Modal show={showModalLocal} onClose={() => setShowModalLocal(false)} title={currentItemLocal ? 'Editar Local' : 'Novo Local'} size="md">
        <form onSubmit={handleSubmitLocal} className="space-y-4">
          <div>
            <label>Nome do Local</label>
            <input name="nome" value={formDataLocal.nome || ''} onChange={handleFormChangeLocal} required className="w-full border p-2 rounded" />
          </div>
          <div>
            <label>Tipo de Estágio</label>
            <select name="tipo" value={formDataLocal.tipo || 'PCO'} onChange={handleFormChangeLocal} required className="w-full border p-2 rounded">
              <option value="PCO">PCO (Operacional)</option>
              <option value="PJ">PJ (Jurídica)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label>Latitude</label>
                <input name="latitude" value={formDataLocal.latitude || ''} onChange={handleFormChangeLocal} placeholder="-23.xxxxx" className="w-full border p-2 rounded" />
             </div>
             <div>
                <label>Longitude</label>
                <input name="longitude" value={formDataLocal.longitude || ''} onChange={handleFormChangeLocal} placeholder="-46.xxxxx" className="w-full border p-2 rounded" />
             </div>
          </div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalLocal(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>

      <Modal show={showModalGerenciador} onClose={() => setShowModalGerenciador(false)} title={currentItemGerenciador ? 'Editar Gerenciador' : 'Novo Gerenciador'} size="md">
        <form onSubmit={handleSubmitGerenciador} className="space-y-4">
          <div><label>Nome</label><input name="nome" value={formDataGerenciador.nome || ''} onChange={handleFormChangeGerenciador} required className="w-full border p-2 rounded" /></div>
          <div><label>CPF (só números)</label><input name="cpf" value={formDataGerenciador.cpf || ''} onChange={handleFormChangeGerenciador} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItemGerenciador && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalGerenciador(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>

      <Modal show={showModalSoe} onClose={() => setShowModalSoe(false)} title={currentItemSoe ? 'Editar Usuário SOE' : 'Novo Usuário SOE'} size="md">
        <form onSubmit={handleSubmitSoe} className="space-y-4">
          <div><label>Nome</label><input name="nome" value={formDataSoe.nome || ''} onChange={handleFormChangeSoe} required className="w-full border p-2 rounded" /></div>
          <div><label>CPF (só números)</label><input name="cpf" value={formDataSoe.cpf || ''} onChange={handleFormChangeSoe} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItemSoe && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalSoe(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button></div>
        </form>
      </Modal>

      <Modal show={showModalEncomendas} onClose={() => setShowModalEncomendas(false)} title={currentItemEncomendas ? 'Editar Escala (Encomendas)' : 'Nova Escala (Encomendas)'} size="md">
        <form onSubmit={handleSubmitEncomendas} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700">Data da Escala</label><input name="data" type="date" value={formDataEncomendas.data || ''} onChange={handleFormChangeEncomendas} required className="w-full border p-2 rounded" disabled={processing || !!currentItemEncomendas} />{!!currentItemEncomendas && (<p className="text-xs text-gray-500 mt-1">Para alterar a data, exclua esta entrada e crie uma nova.</p>)}</div>
          <div><label className="block text-sm font-medium text-gray-700">CPF Comandante da Guarda</label><input name="cpf_cg" value={formDataEncomendas.cpf_cg || ''} onChange={handleFormChangeEncomendas} required className="w-full border p-2 rounded" placeholder="111.222.333-44" maxLength="11"/></div>
          <div><label className="block text-sm font-medium text-gray-700">CPF Auxiliar Cmt. da Guarda</label><input name="cpf_aux_cg" value={formDataEncomendas.cpf_aux_cg || ''} onChange={handleFormChangeEncomendas} required className="w-full border p-2 rounded" placeholder="555.666.777-88" maxLength="11"/></div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModalEncomendas(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar Escala"}</Button></div>
        </form>
      </Modal>

      {/* MODAL CMT CIA ATUALIZADO */}
      <Modal show={showModalCia} onClose={() => setShowModalCia(false)} title={currentItemCia ? 'Editar Cmt Companhia' : 'Novo Cmt Companhia'} size="md">
        <form onSubmit={handleSubmitCia} className="space-y-4">
          <div><label>Nome do Comandante</label><input name="nome" value={formDataCia.nome || ''} onChange={handleFormChangeCia} required className="w-full border p-2 rounded" /></div>
          <div><label>CPF (só números)</label><input name="cpf" value={formDataCia.cpf || ''} onChange={handleFormChangeCia} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItemCia && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
          <div>
            <label>Companhia Comandada</label>
            <select name="cia_id" value={formDataCia.cia_id || ''} onChange={handleFormChangeCia} required className="w-full border p-2 rounded">
              <option value="">Selecione...</option>
              {companhias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            {companhias.length === 0 && <p className="text-xs text-red-500 mt-1">Cadastre as companhias primeiro no painel de Gerenciar Companhias.</p>}
          </div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button onClick={() => setShowModalCia(false)} variant="secondary" disabled={processing}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </Modal>
      
      <Modal show={showModalCmt} onClose={() => setShowModalCmt(false)} title={currentItemCmt ? 'Editar Cmt Pelotão' : 'Novo Cmt Pelotão'} size="md">
        <form onSubmit={handleSubmitCmt} className="space-y-4">
          <div><label>Nome</label><input name="nome" value={formDataCmt.nome || ''} onChange={handleFormChangeCmt} required className="w-full border p-2 rounded" /></div>
          <div><label>CPF (só números)</label><input name="cpf" value={formDataCmt.cpf || ''} onChange={handleFormChangeCmt} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItemCmt && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
          <div>
            <label className="block mb-2 font-medium">Pelotões Comandados</label>
            <div className="border p-3 rounded max-h-40 overflow-y-auto bg-gray-50 space-y-2">
              {pelotoes.map(p => (
                <label key={p.id} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    value={p.id}
                    checked={Array.isArray(formDataCmt.pelotoes_ids) ? formDataCmt.pelotoes_ids.includes(p.id) : false}
                    onChange={() => handleTogglePelotaoCmt(p.id, p.nome)}
                  />
                  <span className="text-sm font-medium text-gray-700">{p.nome}</span>
                </label>
              ))}
            </div>
            {(!Array.isArray(formDataCmt.pelotoes_ids) || formDataCmt.pelotoes_ids.length === 0) && (
              <p className="text-xs text-red-500 mt-1">Selecione pelo menos um pelotão.</p>
            )}
          </div>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button onClick={() => setShowModalCmt(false)} variant="secondary" disabled={processing}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </Modal>
      
      <Modal show={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Exclusão" size="sm">
        <p>Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.</p>
        {deleteAction?.itemType === 'escolha' && (<p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded-md my-3">Atenção: A exclusão de uma **Escolha** fará com que o cadete volte para o estado de "Aguardando Escolha" e seja re-enquadrado na fila de acordo com a ordem de antiguidade do pelotão.</p>)}
        {error && <p className="text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-3 mt-4"><Button onClick={() => { setShowConfirm(false); setError(''); }} variant="secondary">Cancelar</Button><Button onClick={handleDelete} variant="danger">Excluir</Button></div>
      </Modal>

    </div>
  );
};